import type { ElementType, HTMLAttributes, ReactElement } from 'react';

import { classNames } from '@m1work/shared-utils';

import styles from './Heading.module.scss';

export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;
export type HeadingSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
export type HeadingWeight = 'snug' | 'normal' | 'medium' | 'semibold' | 'bold';
export type HeadingAlign = 'left' | 'center' | 'right';
export type HeadingLineHeight = 'tight' | 'snug' | 'normal' | 'relaxed' | 'loose';

export interface HeadingProps extends HTMLAttributes<HTMLHeadingElement> {
    /** Heading level: renders `h1`…`h6`; pick it by the document outline, not by the look. @default 2 */
    level?: HeadingLevel;
    /** Visual size; when not set, it follows the level (`h1` → `4xl` … `h6` → `md`). */
    size?: HeadingSize;
    /** Font weight. @default 'semibold' */
    weight?: HeadingWeight;
    /** Horizontal alignment; inherited when not set. */
    align?: HeadingAlign;
    /** Line height; the size's own when not set. */
    lineHeight?: HeadingLineHeight;
}

const DEFAULT_SIZE_BY_LEVEL: Record<HeadingLevel, HeadingSize> = {
    1: '4xl',
    2: '3xl',
    3: '2xl',
    4: 'xl',
    5: 'lg',
    6: 'md',
};

/**
 * A heading. The level sets the HTML tag (document outline), the size sets the look, so they can differ.
 *
 * @example
 * <Heading level={1} size="xl" weight="semibold">
 *     {offer.name}
 * </Heading>
 */
export function Heading({
    level = 2,
    size,
    weight = 'semibold',
    align,
    lineHeight,
    className,
    ...props
}: HeadingProps): ReactElement {
    const HeadingTag: ElementType = `h${level}`;
    const resolvedSize = size ?? DEFAULT_SIZE_BY_LEVEL[level];

    return (
        <HeadingTag
            className={classNames(
                styles.heading,
                styles[`size-${resolvedSize}`],
                styles[`weight-${weight}`],
                align && styles[`align-${align}`],
                lineHeight && styles[`line-height-${lineHeight}`],
                className,
            )}
            {...props}
        />
    );
}
